import { DataPointsApiResponse } from '@tableau/metrics-engine';
import { MetricDetailView, MetricDetailViewProps, MetricRefreshTime, MetricTitle } from '@tableau/metrics-shared-widgets-dom';
import { TextLinkWidget } from '@tableau/widgets';
import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { classes, media, style } from 'typestyle';
import _ from 'underscore';

import { setSpaceInfoItem } from '../../../app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../../app/actions/spaces/FavoritesActions';
import { getAppStore } from '../../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../../app/content/breadcrumbs/BreadcrumbHelpers';
import { SessionSiteSelectors } from '../../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { ActionsMenu } from '../../../app/spaces/ActionsMenu/ActionsMenu';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { RouterError } from '../../../app/spaces/RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../../../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../../../app/spaces/SpacesSiteUrls';
import { AppState } from '../../../app/state/AppState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../../app/state/SpaceInfo';
import { UrlParamKeys } from '../../../app/UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../../app/utils/ConnectUtils';
import { currentUserIsOwnerOrAdmin } from '../../../app/utils/MixedContentHelpers';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { showActionableToast } from '../../../common/react/components/ActionableMessage';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { InterpolatedString } from '../../../common/react/components/InterpolatedString';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { Projects, ProjectsResource } from '../../../projects/resources/Projects';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Helpers } from '../../../ts/helpers/Helpers';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { replaceMetricDataRefreshList, resumeMetric, updateMetricsDataByIds } from '../../actions/UpdateMetricsActions';
import { metricDetailProps } from '../../metricCardPropsFromDataPoints';
import { IMetricInfo, IMetricInfoWithoutData } from '../../models/IMetricInfo';
import { Metrics } from '../../resources/Metrics';
import { selectMetricDataForMetricId } from '../../selectors';
import { ConnectedMetricDetailShareButton } from './MetricDetailShareButton';
import { ConnectedMetricMetadataPanel } from './MetricMetadataPanel';
import { ConnectedMetricsBanner } from './MetricsBanner';
import { BreakPoints } from './MetricsStyles';

const DETAIL_VIEW_MIN_HEIGHT = 320;
const DETAIL_VIEW_MAX_HEIGHT = 700;

export interface MetricDetailSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly id: string;
  readonly routeLocationSearch: string;
  readonly renderMetricDetailSpace: (state: MetricDetailSpaceState, metricDataProp: DataPointsApiResponse, detailHolder: React.RefObject<HTMLDivElement>, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls, metadataChanged?: () => void) => JSX.Element;
  readonly userCanResumeMetric: (metricInfo: IMetricInfo) => boolean;
  readonly showResumeMetricWarning: (metricInfo: IMetricInfo, resumeMetricHelpLink: string, translate: IReactTranslateService, resumeMetric: (metricInfo: IMetricInfo) => void) => void;
  readonly showSuspendedMetricWarning: () => void;
}

export interface MetricDetailSpaceAngularProps {
  readonly projects: ProjectsResource;
  readonly resumeMetricHelpLink: string;
  readonly translate: IReactTranslateService;
}

export interface MetricDetailSpaceDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly resumeMetric: (metricInfo: IMetricInfo) => void;
  readonly updateMetricsDataByIds: (ids: string[]) => void;
  readonly replaceMetricDataRefreshList: (ids: string[]) => void;
}

export interface MetricDetailSpaceStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly metricData?: DataPointsApiResponse;
}

export interface MetricDetailSpaceState {
  readonly metricInfo: IMetricInfoWithoutData;
  readonly hasError: boolean;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly errorCode: number;
  readonly detailHeight: number;
}

const InitialState: MetricDetailSpaceState = {
  metricInfo: null,
  hasError: false,
  initialRender: true,
  renderChildren: false,
  errorCode: undefined,
  detailHeight: DETAIL_VIEW_MIN_HEIGHT,
};


export type MetricDetailSpacePropsWithoutAngular = MetricDetailSpaceOwnProps & MetricDetailSpaceDispatchProps & MetricDetailSpaceStateProps;
export type MetricDetailSpaceProps = MetricDetailSpacePropsWithoutAngular & MetricDetailSpaceAngularProps & MetricDetailSpaceStateProps;

export class MetricDetailSpace extends React.PureComponent<MetricDetailSpaceProps, MetricDetailSpaceState> {
  private unmounted: boolean = false;
  private spaceInfo: SpaceInfo = undefined;
  private detailHolder: React.RefObject<HTMLDivElement>;

  public constructor(props: MetricDetailSpaceProps) {
    super(props);
    this.state = InitialState;
    this.spaceInfo = { spaceType: 'metric', subspaceName: undefined, item: SpaceItemForNonEntitySpace /* populated after fetching data */ };
    this.detailHolder = React.createRef();
    if (!this.props.metricData) {
      // go fetch data, we weren't provided any.
      props.updateMetricsDataByIds([this.props.id]);
    }
    if (FeatureFlags.metricsAutoUpdateEnabled) {
      props.replaceMetricDataRefreshList([this.props.id]);
    }
  }

  private updateDetailHeight() {
    if (!this.detailHolder.current) {
      return;
    }

    const desiredHeight = this.detailHolder.current.getBoundingClientRect().height;
    const resolvedHeight = Math.max(Math.min(desiredHeight, DETAIL_VIEW_MAX_HEIGHT), DETAIL_VIEW_MIN_HEIGHT);
    this.setState({
      detailHeight: resolvedHeight,
    });
  }

  private windowSizeChanged = () => {
    this.updateDetailHeight();
  };

  public componentDidMount() {
    this.unmounted = false;
    this.fetchMetric();
    window.addEventListener('resize', this.windowSizeChanged);
    this.updateDetailHeight();
  }

  public componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('resize', this.windowSizeChanged);
  }

  public componentDidUpdate(prevProps: MetricDetailSpaceProps) {
    this.updateDetailHeight();
    if (this.changedMetric(prevProps)) {
      this.props.updateMetricsDataByIds([this.props.id]);
      this.updateState(InitialState /* want the initial state for the metric */);
      this.fetchMetric();
      return;
    }

    // if we don't have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender && this.state.metricInfo) {
      if(!this.props.metricData){
        this.updateState({ hasError: true, renderChildren: false });
      }
      this.updateState({ initialRender: false, renderChildren: true });

      // Update recently viewed and the metric view count
      Metrics.markRecentlyViewed(this.state.metricInfo.id);
      Metrics.markViewed(this.state.metricInfo.id);

      // Show warning toast if metric is suspended
      const values = queryString.parse(this.props.routeLocationSearch);
      const isResumeBannerSuppressed = Helpers.BooleanQueryParamRegex.test(values[UrlParamKeys.SuppressResumeMetricBanner]);
      if (this.state.metricInfo.suspended && !isResumeBannerSuppressed) {
        this.props.userCanResumeMetric(this.state.metricInfo) ?
          this.props.showResumeMetricWarning(this.state.metricInfo, this.props.resumeMetricHelpLink, this.props.translate, this.props.resumeMetric) :
          this.props.showSuspendedMetricWarning();
      }
    }
  }

  private updateState<K extends keyof MetricDetailSpaceState>(newState: Pick<MetricDetailSpaceState, K>): void {
    if (!this.unmounted) {
      this.setState(newState, this.updateTitle);
    }
  }

  private updateTitle() {
    if (this.state.metricInfo) {
      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.metricInfo.name });
    } else {
      BrowserTitleService.setTitleToServerName();
    }
  }

  private changedMetric(prevProps: MetricDetailSpaceProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.id) &&
      (prevProps.siteName !== this.props.siteName || prevProps.id !== this.props.id);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.metricInfo);
  }

  private fetchMetric = async () => {
    try {
      const metric: IMetricInfoWithoutData = await Metrics.getById(this.props.id);
      if (metric) {
        this.spaceInfo = { item: { ...metric, contentType: 'metric', id: this.props.id }, spaceType: 'metric', subspaceName: undefined };
        this.props.setSpaceInfoItem(this.spaceInfo);
        this.updateState({ metricInfo: metric });

        const projectHierarchy = await this.props.projects.getProjectHierarchy(metric.project.id);
        const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
        populateContentBreadcrumbs([...projectHierarchyWithType, metric], this.props.urls);

      } else {
        this.updateState({ hasError: true, renderChildren: false });
      }
    } catch (e) {
      this.updateState({ hasError: true, renderChildren: false, errorCode: e as number });
    }
  }

  private addFavorite = () => {
    if (this.state.metricInfo) {
      this.updateState({ metricInfo: { ...this.state.metricInfo, favorite: true }});
      this.props.addContentFavorite(this.state.metricInfo);
    }
  }

  private removeFavorite = () => {
    if (this.state.metricInfo) {
      this.updateState({ metricInfo: { ...this.state.metricInfo, favorite: false }});
      this.props.removeContentFavorite(this.state.metricInfo);
    }
  }

  private metadataChanged = () => {
    this.fetchMetric();
  };

  public render() {
    let element: JSX.Element;
    if (this.state.renderChildren) {
      element = this.props.renderMetricDetailSpace(this.state, this.props.metricData, this.detailHolder, this.addFavorite, this.removeFavorite, this.props.urls, this.metadataChanged);
    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.id} contentType={'metric'} errorCode={this.state.errorCode} />;
      }
      element = <RouterError errorType={'metricDetail'} urls={this.props.urls} />;
    } else {
      element = null;
    }

    return (
      <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
        {element}
      </ConnectedSpaceInfoProvider>);
  }
}

export function renderMetricDetailSpace(state: MetricDetailSpaceState, metricDataProp: DataPointsApiResponse, detailHolder: React.RefObject<HTMLDivElement>, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls, metadataChanged?: () => void) {
  const p = metricDetailProps(state.metricInfo.name, metricDataProp);

  return (
    <div style={metricDetailSpaceContainerStyle}>
      <ConnectedMetricsBanner />
      <div style={containerStyle} role="main">
        {renderTopRow(p, state.metricInfo, addFavorite, removeFavorite)}
        <div className={graphAndMetadataContainerStyle} ref={detailHolder}>
          <div className={timeSeriesContainerWithMarginStyle}>
            <MetricDetailView {...p} height={`${state.detailHeight}px`} />
          </div>
          <div className={metadataContainerStyle} role="complementary">
            <ConnectedMetricMetadataPanel metricInfo={state.metricInfo} schema={metricDataProp?.definition.schema} urls={urls} metadataChanged={metadataChanged}/>
          </div>
        </div>
      </div>
    </div>
  );
}

export function userCanResumeMetric(metricInfo: IMetricInfo) {
  return currentUserIsOwnerOrAdmin(getAppStore().getState(), metricInfo);
}

export function showResumeMetricWarning(metricInfo: IMetricInfo, resumeMetricHelpLink: string, translate: IReactTranslateService, resumeMetric: (metricInfo: IMetricInfo) => void) {
  const learnMoreLink = (
    <TextLinkWidget
      href={resumeMetricHelpLink}
      target="_blank"
      testId="metrics-resume-help"
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  const suspendedMetricString = (
    <InterpolatedString
      baseStringKey="metricSuspendedWithResumeTooltip"
      translate={translate}
      replacements={{
        'LEARN_MORE_LINK': () => learnMoreLink,
      }}
    />
  );

  showActionableToast({
    // We leave the message prop empty because our string is wrapped in a
    // InterpolatedString component which we pass to the children prop.
    message: '',
    children: suspendedMetricString,
    testId: `suspended-metric-warning`,
    actionMessage: Messages.metricResumeButtonLabel(),
    action: () => resumeMetric(metricInfo)
  }, ToastType.Warning);
}

export function showSuspendedMetricWarning() {
  const service = ToasterService.instance;
  service.pop({
    type: ToastType.Warning,
    text: Messages.metricSuspendedNoResumeTooltip(),
    testId: `suspended-metric-nonowner-warning`
  });
}

function renderTopRow(metricDetailProps: MetricDetailViewProps, metricInfoWithoutData: IMetricInfoWithoutData, addFavorite: () => void, removeFavorite: () => void) {
  return (
    <div className={topRowContainerWithMarginStyle}>
      <div style={refreshTimeShareButtonRowLayoutStyle}>
        <div style={refreshTimeStyle}>
          <MetricRefreshTime lastRefreshedTimestamp={metricDetailProps.lastUpdatedTimestamp} />
        </div>
        <div className={shareLayoutStyle}>
          <ConnectedMetricDetailShareButton metricInfo={metricInfoWithoutData} />
        </div>
      </div>
      <div style={titleAndActionsContainerStyle}>
        <div style={titleLayoutStyle}>
          <MetricTitle metricName={metricDetailProps.metricName} />
        </div>
        <div style={favoriteLayoutStyle}>
          <FavoriteToggle isFavorite={metricInfoWithoutData.favorite} onAddFavorite={addFavorite} onRemoveFavorite={removeFavorite} isTabbable />
        </div>
        <div style={actionsLayoutStyle}>
          <ActionsMenu targetItems={[metricInfoWithoutData]} useIcon />
        </div>
      </div>
    </div>
  );
}

export namespace MetricDetailSpaceAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const TranslateService = ServiceLocator.get<ng.translate.ITranslateService>('$translate');

    return {
      projects: Projects,
      resumeMetricHelpLink: HelpService.getServerHelpUrlByCategory('metrics-suspend'),
      translate: TranslateService.instant,
    };
  }

  function selectPropsFromAngularServices(angularServices: MetricDetailSpaceAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MetricDetailSpacePropsWithoutAngular, MetricDetailSpaceAngularProps, MetricDetailSpaceAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const MetricDetailSpaceAng: React.ComponentType<MetricDetailSpacePropsWithoutAngular> = MetricDetailSpaceAngularProvider.HOC(MetricDetailSpace);

const mapDispatchToProps: MapDispatchToProps<MetricDetailSpaceDispatchProps> = (dispatch) => bindActionCreators({
  addContentFavorite: addContentFavorite,
  removeContentFavorite: removeContentFavorite,
  setSpaceInfoItem: setSpaceInfoItem,
  resumeMetric: resumeMetric,
  updateMetricsDataByIds: updateMetricsDataByIds,
  replaceMetricDataRefreshList: replaceMetricDataRefreshList
}, dispatch);

const mapStateToProps: MapStateToProps<MetricDetailSpaceStateProps> = (state: AppState, ownProps: MetricDetailSpaceProps) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  metricData: selectMetricDataForMetricId(state, ownProps.id),
});

export const ConnectedMetricDetailSpace: React.ComponentClass<MetricDetailSpaceOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(MetricDetailSpaceAng);

const metricDetailSpaceContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
  height: '100%'
};

const containerStyle: React.CSSProperties = {
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
};

const metadataContainerStyle: string = style(
  {
    marginRight: '24px',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { marginLeft: '24px' }),
);

const leftMarginStyle: string = style(
  {
    marginLeft: '24px'
  },
  media({ minWidth: BreakPoints.LARGE + 1 }, { marginLeft: '56px' }),
);

const graphAndMetadataContainerStyle: string = style(
  {
    display: 'flex',
    overflow: 'auto',
    height: '100%',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { flexDirection: 'column' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { flexDirection: 'row' }),
);

const timeSeriesContainerStyle: string = style(
  media({ maxWidth: BreakPoints.LARGE }, { marginRight: '40px' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { flex: 4 }),
);

const topRowContainerStyle: string = style(
  {
    paddingTop: '24px',
    marginRight: '24px',
    minHeight: 'fit-content'
  }
);

const timeSeriesContainerWithMarginStyle: string = classes(leftMarginStyle, timeSeriesContainerStyle);
const topRowContainerWithMarginStyle: string = classes(leftMarginStyle, topRowContainerStyle);

const titleAndActionsContainerStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden',
  marginTop: '10px',
  alignItems: 'center',
};
const refreshTimeShareButtonRowLayoutStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  alignItems: 'center',
};
const refreshTimeStyle: React.CSSProperties = {
  flex: '1 1 auto',
};
const titleLayoutStyle: React.CSSProperties = {
  minWidth: 0,
  marginRight: '24px',
  overflow: 'hidden',
};
const favoriteLayoutStyle: React.CSSProperties = {
  marginRight: '12px',
  flexShrink: 0,
};
const actionsLayoutStyle: React.CSSProperties = {
  flexShrink: 0,
};
const shareLayoutStyle: string = style(
  {
    alignSelf: 'flex-start',
    paddingLeft: '12px',
  },
  media({ maxWidth: 375 }, { display: 'none' })
);
